import { formatMoney, formatNumber, KpiGrid, KpiTile, MoneyText } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useStockOverview } from '../../dashboard/stock.ts';
import { usePageContext } from '../../usePageContext.ts';
import { useTenantDetail } from '../useCatalogQueries.ts';
import type { ProductCounts, ProductView } from './product-filters.ts';
import styles from './ProductList.module.css';

export type ProductKpisProps = {
  api: TypedClient;
  company: string | null;
  optionValueId: string | null;
  counts: ProductCounts | undefined;
  onShowView: (view: ProductView) => void;
};

export const ProductKpis = ({ api, company, optionValueId, counts, onShowView }: ProductKpisProps) => {
  const { t, locale } = usePageContext();
  const overview = useStockOverview(api, company, { optionValueId }).data;
  const stock = overview?.totals;
  const depots = overview?.depots.length;
  const currency = useTenantDetail(api).data?.currency;
  const count = (value: number | undefined) =>
    value === undefined ? undefined : formatNumber(value, locale);
  const showAction = (view: ProductView) => (
    <button
      type="button"
      className={styles['kpiLink']}
      onClick={() => {
        onShowView(view);
      }}
    >
      {t('productList.kpi_show')}
    </button>
  );
  return (
    <KpiGrid columns={4}>
      <KpiTile
        emphasis="hero"
        label={t('productList.kpi_saleValue')}
        loading={stock === undefined || currency === undefined}
        value={
          stock === undefined || currency === undefined ? undefined : (
            <MoneyText money={{ amount: stock.saleValue, currency }} locale={locale} />
          )
        }
        details={
          stock?.value == null || currency === undefined
            ? undefined
            : [
                {
                  label: t('productList.kpi_costValue'),
                  value: formatMoney({ amount: stock.value, currency }, locale),
                },
              ]
        }
        hint={
          stock === undefined || depots === undefined
            ? undefined
            : t('productList.kpi_unitsInDepots', {
                count: stock.available,
                units: formatNumber(stock.available, locale),
                depots: t('productList.kpi_depots', { count: depots, total: formatNumber(depots, locale) }),
              })
        }
      />
      <KpiTile
        label={t('productList.kpi_active')}
        loading={counts === undefined}
        value={count(counts?.active)}
        hint={
          counts === undefined
            ? undefined
            : t('productList.kpi_ofCatalogue', { count: counts.all, total: formatNumber(counts.all, locale) })
        }
      />
      <KpiTile
        label={t('productList.kpi_low')}
        tone="warning"
        loading={counts === undefined}
        value={count(counts?.lowStock)}
        action={showAction('low')}
      />
      <KpiTile
        label={t('productList.kpi_out')}
        tone="danger"
        loading={counts === undefined}
        value={count(counts?.outOfStock)}
        action={showAction('out')}
      />
    </KpiGrid>
  );
};
